import { useEffect, useMemo, useState } from 'react';

type Row = Record<string, unknown>;

/**
 * Values saved by inline grid columns, keyed by the grid's row object. The grid doesn't re-fetch
 * its rows after an inline save, so other consumers of the row (e.g. the row form action) read
 * these to see the saved values. Keying by the row object means the edits are dropped as soon as
 * the grid re-fetches and replaces its rows with fresh data from the server.
 */
const edits = new WeakMap<Row, Row>();
const listeners = new Set<() => void>();

export function publishRowEdit(row: Row, values: Row) {
  edits.set(row, { ...edits.get(row), ...values });
  listeners.forEach(listener => listener());
}

/**
 * Returns the row with any values saved by inline grid columns applied, re-rendering when they
 * change. Returns the row itself when there are no edits.
 */
export function useRowWithEdits<T extends Row>(row: T): T {
  const [rowEdits, setRowEdits] = useState(() => edits.get(row));

  useEffect(() => {
    const update = () => setRowEdits(edits.get(row));
    update();
    listeners.add(update);
    return () => {
      listeners.delete(update);
    };
  }, [row]);

  return useMemo(
    () => (rowEdits ? { ...row, ...rowEdits } : row),
    [row, rowEdits]
  );
}
